import { ModelDef } from '../../CORE/src/common/$models/ModelDef'
import { yhcAreasByCode } from '../constants/area'
import {
  buildYhcAreaCodeSelections,
  fetchCitySelectionsForPrefCode,
  formatPrefCodeLabel,
} from '../services/YhcServiceLevelList/serviceLevelRevisionAdminUtils'
import {
  formatRevisionTypeLabel,
  formatWeekdaysForAdminLabel,
  parseWeekdays,
  SERVICE_LEVEL_REVISION_TIME_BAND_SELECTIONS,
  SERVICE_LEVEL_REVISION_TYPE_SELECTIONS,
  SERVICE_LEVEL_REVISION_WEEKDAY_SELECTIONS,
} from '../services/YhcServiceLevelList/serviceLevelRevisionUtils'
import type { ModelYhcServiceLevelRevision } from './yhcDataTypes'

/**
 * サービスレベル検索ページに表示する市区町村単位のサービス改定
 * - 公開 Web: 改定日前のみ、サービス表のラベル横に赤字注釈
 * - 管理画面: CRUD
 */
export const yhc_service_level_revisions: ModelDef<ModelYhcServiceLevelRevision> = {
  tableName: 'yhc_service_level_revisions',
  tableLabel: 'サービス改定情報',
  primaryKeyColType: 'UUID',
  defaultSort: { key: 'revision_date', order: 'desc' },
  columns: {
    title: {
      type: 'STRING',
      label: '管理用タイトル',
    },
    revision_type: {
      type: 'STRING',
      label: '改定種別',
      validate: { notEmpty: true },
      selections: () => SERVICE_LEVEL_REVISION_TYPE_SELECTIONS,
      customLabel: (value) => formatRevisionTypeLabel(value),
      strictSelections: true,
      width: { xs: 8 },
    },
    pref_code: {
      type: 'STRING',
      label: '都道府県',
      validate: { notEmpty: true },
      selections: () => buildYhcAreaCodeSelections(),
      customLabel: (value) => formatPrefCodeLabel(value),
      strictSelections: true,
      editCallback: ({ row, newValue, oldValue }) => {
        if (newValue !== oldValue) {
          row.city = null
        }
        return row
      },
      width: { xs: 8 },
    },
    city: {
      type: 'STRING',
      label: '市区郡',
      validate: { notEmpty: true },
      dynamicSelections: true,
      strictSelections: true,
      enableIf: (record) => !!record?.pref_code,
      selections: async (record) => {
        if (!record?.pref_code) return []
        return fetchCitySelectionsForPrefCode(record.pref_code)
      },
      width: { xs: 8 },
    },
    revision_date: {
      type: 'DATEONLY',
      label: '改定日',
      validate: { notEmpty: true },
      inputHelpText:
        'この日から新サービスが適用されます。注釈は改定日の前日までサービスレベル検索に表示されます',
    },
    value_after_weekdays: {
      type: 'MULTISELECT',
      label: '変更後の営業曜日',
      enableIf: (record) => record?.revision_type === 'working_days',
      inputComponent: 'MultiselectCheckboxesInput',
      selections: () => [...SERVICE_LEVEL_REVISION_WEEKDAY_SELECTIONS],
      // チェックボックス各選択肢は曜日のみ。一覧セル（複数曜日）だけ週N表記にする
      customLabel: (value) => {
        if (
          typeof value === 'string' &&
          (SERVICE_LEVEL_REVISION_WEEKDAY_SELECTIONS as readonly string[]).includes(
            value,
          )
        ) {
          return value
        }
        return formatWeekdaysForAdminLabel(value)
      },
      width: { xs: 24 },
      strictSelections: true,
      inputHelpText:
        '営業する曜日を選択してください。週7の場合は全曜日を選択してください。',
    },
    value_after: {
      type: 'STRING',
      label: '変更後の時間帯区分',
      enableIf: (record) => record?.revision_type === 'time_band',
      selections: () => SERVICE_LEVEL_REVISION_TIME_BAND_SELECTIONS,
      strictSelections: true,
      inputHelpText: '変更後の時間帯区分を選択してください',
    },
    value_after_lead_time_e: {
      type: 'STRING',
      label: '変更後リードタイム（Eランク以下）',
      enableIf: (record) => record?.revision_type === 'lead_time',
      inputHelpText: 'Eランク以下の変更後リードタイム（任意。E/FGのどちらか一方以上必須）',
    },
    value_after_lead_time_fg: {
      type: 'STRING',
      label: '変更後リードタイム（FGランク）',
      enableIf: (record) => record?.revision_type === 'lead_time',
      inputHelpText: 'FGランクの変更後リードタイム（任意。E/FGのどちらか一方以上必須）',
    },
    memo: {
      type: 'TEXT',
      label: '管理用メモ',
      width: { xs: 48 },
    },
  },
  async beforeSave(saving) {
    if (!saving.pref_code || !(saving.pref_code in yhcAreasByCode)) {
      throw new Error('都道府県を選択してください')
    }
    if (!saving.city) {
      throw new Error('市区郡を選択してください')
    }
    if (!saving.revision_date) {
      throw new Error('改定日を入力してください')
    }
    if (!saving.revision_type) {
      throw new Error('改定種別を選択してください')
    }

    if (saving.revision_type === 'working_days') {
      const weekdays = parseWeekdays(saving.value_after_weekdays)
      if (weekdays.length < 1) {
        throw new Error('変更後の営業曜日を1つ以上選択してください')
      }
      saving.value_after_weekdays = weekdays
      saving.value_after = null
      saving.value_after_lead_time_e = null
      saving.value_after_lead_time_fg = null
    }

    if (saving.revision_type === 'time_band') {
      if (!saving.value_after) {
        throw new Error('変更後の時間帯区分を選択してください')
      }
      saving.value_after_weekdays = null
      saving.value_after_lead_time_e = null
      saving.value_after_lead_time_fg = null
    }

    if (saving.revision_type === 'lead_time') {
      const hasE = !!(saving.value_after_lead_time_e || '').trim()
      const hasFg = !!(saving.value_after_lead_time_fg || '').trim()
      if (!hasE && !hasFg) {
        throw new Error(
          'リードタイム改定では、Eランク以下またはFGランクのどちらか一方以上を入力してください',
        )
      }
      saving.value_after = null
      saving.value_after_weekdays = null
    }

    // 最新マスタに市区郡が存在するか検証
    const cityOptions = await fetchCitySelectionsForPrefCode(saving.pref_code)
    if (!cityOptions.some((opt) => opt.value === saving.city)) {
      throw new Error(
        `市区郡「${saving.city}」が最新の郵便番号マスタに見つかりません。都道府県・市区郡を再選択してください`,
      )
    }

    return saving
  },
}
